import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Upload, FileText, User } from "lucide-react";
import toast from "react-hot-toast";

const inputStyle = {
  width: "100%", boxSizing: "border-box", height: 38, padding: "0 12px",
  background: "#F9FAFB", border: "1.5px solid #E5E7EB", borderRadius: 8,
  fontSize: 13.5, color: "#111827", outline: "none",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#6B7280", marginBottom: 6, display: "block" };
const sectionTitle = { fontSize: 12.5, fontWeight: 700, color: "#4F46E5", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 10, marginTop: 4 };
const row2 = { display: "flex", gap: 12 };
const col = { flex: 1, minWidth: 0 };

function Field({ label, ...props }) {
  return (
    <div style={col}>
      <label style={labelStyle}>{label}</label>
      <input style={inputStyle} {...props} />
    </div>
  );
}

function FileField({ label, onChange, fileName }) {
  return (
    <div style={col}>
      <label style={labelStyle}>{label}</label>
      <label style={{
        ...inputStyle, display: "flex", alignItems: "center", gap: 8, cursor: "pointer", color: fileName ? "#111827" : "#9CA3AF",
      }}>
        {fileName ? <FileText size={14} color="#4F46E5" /> : <Upload size={14} />}
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fileName || "Choose file…"}</span>
        <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={onChange} style={{ display: "none" }} />
      </label>
    </div>
  );
}

export default function DriverFormModal({ driver, onClose, onSubmit }) {
  const isEdit = !!driver;

  const [name,          setName]          = useState(driver?.name || "");
  const [phone,         setPhone]         = useState(driver?.phone || "");
  const [licenseNumber, setLicenseNumber] = useState(driver?.licenseNumber || "");
  const [licenseExpiry, setLicenseExpiry] = useState(driver?.licenseExpiry ? driver.licenseExpiry.slice(0, 10) : "");
  const [email,               setEmail]               = useState(driver?.email || "");
  const [alternateContact,    setAlternateContact]    = useState(driver?.alternateContact || "");
  const [address,              setAddress]              = useState(driver?.address || "");
  const [city,                 setCity]                 = useState(driver?.city || "");
  const [state,                setState]                = useState(driver?.state || "");
  const [pinCode,              setPinCode]              = useState(driver?.pinCode || "");
  const [dob,                  setDob]                  = useState(driver?.dateOfBirth ? driver.dateOfBirth.slice(0, 10) : "");
  const [bloodGroup,           setBloodGroup]           = useState(driver?.bloodGroup || "");
  const [emergencyContactName, setEmergencyContactName] = useState(driver?.emergencyContactName || "");
  const [emergencyContactPhone, setEmergencyContactPhone] = useState(driver?.emergencyContactPhone || "");
  const [emergencyRelation,    setEmergencyRelation]    = useState(driver?.emergencyRelation || "");
  const [aadhaarNumber,        setAadhaarNumber]        = useState(driver?.aadhaarNumber || "");
  const [panNumber,            setPanNumber]            = useState(driver?.panNumber || "");
  const [medicalCertExpiry,    setMedicalCertExpiry]    = useState(driver?.medicalCertExpiry ? driver.medicalCertExpiry.slice(0, 10) : "");
  const [experienceYears,      setExperienceYears]      = useState(driver?.experienceYears ?? "");
  const [joiningDate,          setJoiningDate]          = useState(driver?.joiningDate ? driver.joiningDate.slice(0, 10) : "");
  const [notes,                setNotes]                = useState(driver?.notes || "");

  const [photo,       setPhoto]       = useState(null);
  const [idProof,     setIdProof]     = useState(null);
  const [licenseDoc,  setLicenseDoc]  = useState(null);
  const [medicalCert, setMedicalCert] = useState(null);

  const [loginEmail,   setLoginEmail]   = useState("");
  const [tempPassword, setTempPassword] = useState("");
  const [sendCredentialsEmail, setSendCredentialsEmail] = useState(true);

  const [saving, setSaving] = useState(false);

  function pick(setter) {
    return e => {
      const f = e.target.files?.[0];
      if (!f) return;
      if (f.size > 10 * 1024 * 1024) return toast.error("File is too big — please keep it under 10 MB");
      setter(f);
    };
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const fd = new FormData();
      const fields = {
        name: name.trim(), phone, licenseNumber, licenseExpiry, email, alternateContact,
        address, city, state, pinCode, dob, bloodGroup, emergencyContactName, emergencyContactPhone,
        emergencyRelation, aadhaarNumber, panNumber, medicalCertExpiry, experienceYears, joiningDate, notes,
      };
      Object.entries(fields).forEach(([k, v]) => fd.append(k, v ?? ""));
      if (photo) fd.append("photo", photo);
      if (idProof) fd.append("idProof", idProof);
      if (licenseDoc) fd.append("licenseDoc", licenseDoc);
      if (medicalCert) fd.append("medicalCert", medicalCert);

      if (!isEdit && loginEmail.trim() && tempPassword.trim()) {
        fd.append("loginEmail", loginEmail.trim());
        fd.append("tempPassword", tempPassword.trim());
        fd.append("sendCredentialsEmail", String(sendCredentialsEmail));
      }

      await onSubmit(fd);
      toast.success(isEdit ? "Driver updated" : "Driver added");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save driver");
      setSaving(false);
    }
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        style={{
          position: "fixed", inset: 0, zIndex: 900,
          background: "rgba(17,24,39,0.55)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        }}
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.div
          initial={{ scale: 0.96, y: 16, opacity: 0 }}
          animate={{ scale: 1,    y: 0,  opacity: 1 }}
          exit={{    scale: 0.96, y: 16, opacity: 0 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          style={{
            width: "100%", maxWidth: 640, maxHeight: "88vh",
            background: "#ffffff", border: "1px solid #E5E7EB",
            borderRadius: 16, overflow: "hidden",
            boxShadow: "0 24px 64px rgba(0,0,0,0.14)",
            display: "flex", flexDirection: "column",
          }}
        >
          <div style={{
            padding: "18px 22px", borderBottom: "1px solid #F3F4F6", flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#111827", display: "flex", alignItems: "center", gap: 8 }}>
              <User size={16} color="#4F46E5" /> {isEdit ? "Edit Driver" : "Add Driver"}
            </div>
            <button
              onClick={onClose}
              style={{ background: "none", border: "1px solid #E5E7EB", borderRadius: 8, width: 32, height: 32, cursor: "pointer", color: "#9CA3AF", display: "flex", alignItems: "center", justifyContent: "center" }}
            ><X size={14} /></button>
          </div>

          <form onSubmit={handleSubmit} style={{ overflow: "auto", padding: "18px 22px", display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={sectionTitle}>Personal Information</div>
            <Field label="Full Name *" value={name} onChange={e => setName(e.target.value)} placeholder="Driver's full name" autoFocus />
            <div style={row2}>
              <Field label="Phone" value={phone} onChange={e => setPhone(e.target.value)} placeholder="10-digit mobile number" />
              <Field label="Alternate Contact" value={alternateContact} onChange={e => setAlternateContact(e.target.value)} />
            </div>
            <div style={row2}>
              <Field label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} />
              <Field label="Date of Birth" type="date" value={dob} onChange={e => setDob(e.target.value)} />
            </div>
            <div style={row2}>
              <Field label="Blood Group" value={bloodGroup} onChange={e => setBloodGroup(e.target.value)} placeholder="e.g. O+" />
              <Field label="Experience (years)" type="number" value={experienceYears} onChange={e => setExperienceYears(e.target.value)} />
            </div>

            <div style={sectionTitle}>Address</div>
            <Field label="Address" value={address} onChange={e => setAddress(e.target.value)} />
            <div style={row2}>
              <Field label="City" value={city} onChange={e => setCity(e.target.value)} />
              <Field label="State" value={state} onChange={e => setState(e.target.value)} />
              <Field label="PIN Code" value={pinCode} onChange={e => setPinCode(e.target.value)} />
            </div>

            <div style={sectionTitle}>Emergency Contact</div>
            <div style={row2}>
              <Field label="Name" value={emergencyContactName} onChange={e => setEmergencyContactName(e.target.value)} />
              <Field label="Phone" value={emergencyContactPhone} onChange={e => setEmergencyContactPhone(e.target.value)} />
              <Field label="Relation" value={emergencyRelation} onChange={e => setEmergencyRelation(e.target.value)} />
            </div>

            <div style={sectionTitle}>Identity & License</div>
            <div style={row2}>
              <Field label="Aadhaar Number" value={aadhaarNumber} onChange={e => setAadhaarNumber(e.target.value)} />
              <Field label="PAN Number" value={panNumber} onChange={e => setPanNumber(e.target.value.toUpperCase())} />
            </div>
            <div style={row2}>
              <Field label="License Number" value={licenseNumber} onChange={e => setLicenseNumber(e.target.value)} />
              <Field label="License Expiry" type="date" value={licenseExpiry} onChange={e => setLicenseExpiry(e.target.value)} />
            </div>
            <div style={row2}>
              <Field label="Medical Cert. Expiry" type="date" value={medicalCertExpiry} onChange={e => setMedicalCertExpiry(e.target.value)} />
              <Field label="Joining Date" type="date" value={joiningDate} onChange={e => setJoiningDate(e.target.value)} />
            </div>

            <div style={sectionTitle}>Documents</div>
            <div style={row2}>
              <FileField label="Driver Photo" onChange={pick(setPhoto)} fileName={photo?.name} />
              <FileField label="ID Proof" onChange={pick(setIdProof)} fileName={idProof?.name} />
            </div>
            <div style={row2}>
              <FileField label="Driving License" onChange={pick(setLicenseDoc)} fileName={licenseDoc?.name} />
              <FileField label="Medical Certificate" onChange={pick(setMedicalCert)} fileName={medicalCert?.name} />
            </div>

            {!isEdit && (
              <>
                <div style={sectionTitle}>Driver Portal Login (optional)</div>
                <div style={row2}>
                  <Field label="Driver Email (login)" type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} placeholder="Required to enable portal login" />
                  <Field label="Temporary Password" value={tempPassword} onChange={e => setTempPassword(e.target.value)} placeholder="Min 6 characters" />
                </div>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#374151", cursor: "pointer" }}>
                  <input type="checkbox" checked={sendCredentialsEmail} onChange={e => setSendCredentialsEmail(e.target.checked)} />
                  Send credentials via email
                </label>
              </>
            )}

            <div style={sectionTitle}>Notes</div>
            <textarea
              value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              style={{ ...inputStyle, height: "auto", padding: "8px 12px", resize: "vertical", fontFamily: "inherit" }}
            />

            <button
              type="submit"
              disabled={saving}
              style={{
                marginTop: 6, width: "100%", height: 42, borderRadius: 10, border: "none",
                background: saving ? "#9CA3AF" : "#4F46E5",
                color: "#fff", fontSize: 14, fontWeight: 700,
                cursor: saving ? "default" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}
            >
              {saving ? <><Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} /> Saving…</> : isEdit ? "Save Changes" : "Add Driver"}
            </button>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
